import {
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
} from 'services/content-references/types'
import { GET_SERIES_REFERENCES, GET_COURSE_REFERENCES } from './queries'

/**
 * Course module reference types (practice, other, etc.).
 * Used to hide these from Content References sections (they are managed in course-module UI).
 */
export const COURSE_MODULE_REFERENCE_TYPES = [
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER,
]

/**
 * List of reference types that should be populated from metadata
 * These are used when grabbing metadata from series or other content
 */
export const REFERENCE_TYPE_KEYS_TO_POPULATE_FROM_METADATA = [
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
]

/**
 * Config for "Copy Metadata from [Source]" button. Each source (series, course) declares
 * its trigger field, query, and which reference types to copy.
 */
export const COPY_METADATA_SOURCES = [
  {
    triggerRefType: CONTENT_REFERENCE_TYPE_SERIES,
    sourceLabel: 'Series',
    query: GET_SERIES_REFERENCES,
    buildVariables: (contentId) => ({ seriesContentId: contentId }),
    extractReferences: (data) => data?.contentList?.content?.[0]?.contentReferencesFrom ?? [],
    refTypesToCopy: REFERENCE_TYPE_KEYS_TO_POPULATE_FROM_METADATA,
  },
  {
    triggerRefType: CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
    sourceLabel: 'Course',
    query: GET_COURSE_REFERENCES,
    buildVariables: (contentId) => ({ courseContentId: contentId }),
    extractReferences: (data) => data?.contentList?.content?.[0]?.contentReferencesFrom ?? [],
    refTypesToCopy: REFERENCE_TYPE_KEYS_TO_POPULATE_FROM_METADATA,
  },
]

/**
 * List of reference types that should be single-select only
 * These fields will not allow multiple selections
 */
export const SINGLE_SELECT_REFERENCE_TYPES = [
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
]

/**
 * List of reference types that should be multi-select
 * These fields will allow multiple selections
 */
export const MULTI_SELECT_REFERENCE_TYPES = [
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  ...COURSE_MODULE_REFERENCE_TYPES,
]

/**
 * List of fields that should be treated as boolean values
 * These fields will be converted to boolean when saving
 */
export const BOOLEAN_FIELDS = [
  'contentSearchNoIndex',
  'excludeFromRecommendations',
  'generatePath',
  'published',
  'seoRobotsNoFollow',
  'seoRobotsNoIndex',
]

/**
 * List of fields that should be treated as numbers
 * These fields will be converted to numbers when saving
 */
export const NUMBER_FIELDS = ['seasonNumber', 'episodeNumber']

/**
 * List of fields that should be treated as textareas
 * These fields will have special handling for empty values
 */
export const TEXTAREA_FIELDS = ['shortDescription', 'description', 'thirdPartyDescription']
